import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAccesAdmin } from './acces';
import { adminFetch, apiErrorMessage, errorMessage } from './adminFetch';
import { parametres, type EtatEspace as Etat } from './espacesParametres';
import { FermerDossier, FermerEspace, PanneauPurges } from './FermetureEspace';
import './AdminEspacesPage.css';

/**
 * Back-office › Espaces (30/10/2026) : espaces, utilisateurs et dossiers de la
 * suite, avec les statistiques — volumes, formules, inscriptions, activité.
 *
 * Réservé au superadmin. Les actions sur un compte (suspendre, réactiver,
 * SIRET de test) se font dans l'écran Comptes, vers lequel chaque utilisateur
 * renvoie. Ici : fermer un espace ou un dossier, et la purge à l'échéance
 * (01/11/2026, `FermetureEspace.tsx`). L'export CSV est journalisé.
 */

type Produit = 'APP' | 'PREVI' | 'COMPTA' | 'IMMO';

interface Formule {
  id: string;
  produit: Produit;
  nom: string;
  gratuite: boolean;
  active: boolean;
  abonnements: number;
  offerts: number;
  quantite: number;
}

interface Statistiques {
  genereLe: string;
  volumes: {
    espaces: { total: number; actifs: number; fermes: number };
    utilisateurs: {
      total: number;
      actifs: number;
      enAttente: number;
      suspendus: number;
      supprimes: number;
      sansEspace: number;
      equipe: number;
    };
    dossiers: { total: number; pro: number; particuliers: number; supprimes: number };
  };
  formules: Formule[];
  produits: { produit: Produit; espacesPayants: number; espacesGratuits: number }[];
  abonnements: { enCours: number; payants: number; offerts: number; impayes: number };
  inscriptions: {
    comptes7j: number;
    comptes30j: number;
    dossiers7j: number;
    dossiers30j: number;
    espaces30j: number;
    parSemaine: { semaine: string; comptes: number; dossiers: number }[];
  };
  activite: {
    connectes7j: number;
    connectes30j: number;
    jamaisConnectes: number;
    adressesNonConfirmees: number;
  };
}

interface AbonnementVue {
  produit: Produit;
  formule: string;
  gratuite: boolean;
  statut: string;
  source: string;
  quantite: number;
  finPeriode: string;
  resiliationEnFin: boolean;
}

interface EspaceListe {
  id: string;
  nom: string;
  slug: string;
  creeLe: string;
  fermeLe: string | null;
  proprietaire: { email: string; nom: string | null } | null;
  membres: number;
  dossiers: number;
  derniereConnexion: string | null;
  abonnements: AbonnementVue[];
}

interface Liste {
  total: number;
  page: number;
  parPage: number;
  pages: number;
  espaces: EspaceListe[];
}

interface Detail {
  id: string;
  nom: string;
  fermeLe: string | null;
  membres: {
    id: string;
    email: string;
    nom: string | null;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    profil: 'STANDARD' | 'LECTURE_SEULE';
    statut: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DELETED';
    derniereConnexion: string | null;
    membreDepuis: string;
  }[];
  dossiers: {
    id: string;
    nom: string;
    type: 'PRO' | 'PARTICULIER';
    siret: string | null;
    codePostal: string | null;
    ville: string | null;
    ape: string | null;
    creeLe: string;
    supprimeLe: string | null;
  }[];
  abonnements: (AbonnementVue & { depuis: string; enCours: boolean })[];
  compteurs: { invitations: number; achatsPacks: number; accesExternes: number };
}

const JOUR = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' });
const NOMBRE = new Intl.NumberFormat('fr-FR');
const date = (iso: string | null) => (iso ? JOUR.format(new Date(iso)) : '—');

const PRODUITS: Record<Produit, string> = {
  APP: 'SEmplyApp',
  PREVI: 'SEmplyPrevi',
  COMPTA: 'SEmplyCompta',
  IMMO: 'SEmplyImmo',
};
const ROLES = { OWNER: 'propriétaire', ADMIN: 'administrateur', MEMBER: 'membre' } as const;
const STATUTS = {
  ACTIVE: 'Actif',
  PENDING: 'Adresse non confirmée',
  SUSPENDED: 'Suspendu',
  DELETED: 'Supprimé',
} as const;
const SOURCES: Record<string, string> = {
  STRIPE: 'payé par Stripe',
  GIFT: 'offert',
  PAYPAL: 'payé par PayPal',
  APPLE: 'App Store',
  GOOGLE: 'Google Play',
};
const STATUTS_ABONNEMENT: Record<string, string> = {
  ACTIVE: 'en cours',
  TRIALING: 'essai',
  PAST_DUE: 'impayé',
  CANCELED: 'résilié',
  INCOMPLETE: 'incomplet',
};

function Chiffre({
  valeur,
  libelle,
  detail,
}: {
  valeur: number;
  libelle: string;
  detail?: string;
}) {
  return (
    <div className="adm-esp-chiffre">
      <strong>{NOMBRE.format(valeur)}</strong>
      <span>{libelle}</span>
      {detail ? <small className="adm-muted">{detail}</small> : null}
    </div>
  );
}

/** Inscriptions par semaine : barres comptes / dossiers, sans bibliothèque. */
function Courbe({ points }: { points: Statistiques['inscriptions']['parSemaine'] }) {
  const max = Math.max(1, ...points.flatMap((p) => [p.comptes, p.dossiers]));
  const resume = points
    .map((p) => `semaine du ${p.semaine} : ${p.comptes} comptes, ${p.dossiers} dossiers`)
    .join(' ; ');
  return (
    <figure className="adm-esp-courbe">
      <div
        className="adm-esp-barres"
        role="img"
        aria-label={`Inscriptions des 12 dernières semaines — ${resume}`}
      >
        {points.map((p) => (
          <div
            key={p.semaine}
            className="adm-esp-semaine"
            title={`Semaine du ${date(p.semaine)} : ${p.comptes} compte(s), ${p.dossiers} dossier(s)`}
          >
            <span
              className="adm-esp-barre adm-esp-barre--comptes"
              style={{ height: `${(p.comptes / max) * 100}%` }}
            />
            <span
              className="adm-esp-barre adm-esp-barre--dossiers"
              style={{ height: `${(p.dossiers / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <figcaption className="adm-esp-legende">
        <span className="adm-esp-puce adm-esp-puce--comptes" /> Comptes
        <span className="adm-esp-puce adm-esp-puce--dossiers" /> Dossiers
        <span className="adm-muted"> — du {date(points[0]?.semaine ?? null)} à cette semaine</span>
      </figcaption>
    </figure>
  );
}

function BlocStatistiques({ stats }: { stats: Statistiques }) {
  const { volumes: v, inscriptions: i, activite: a } = stats;
  const payantes = stats.formules.filter((f) => !f.gratuite && (f.active || f.abonnements > 0));
  return (
    <div className="adm-esp-stats">
      <section className="adm-esp-carte" aria-labelledby="esp-volumes">
        <h2 id="esp-volumes">Volumes</h2>
        <div className="adm-esp-chiffres">
          <Chiffre
            valeur={v.espaces.actifs}
            libelle="espaces actifs"
            detail={`${v.espaces.fermes} fermé(s)`}
          />
          <Chiffre
            valeur={v.utilisateurs.total}
            libelle="utilisateurs"
            detail={`${v.utilisateurs.sansEspace} sans espace · ${v.utilisateurs.suspendus} suspendu(s)`}
          />
          <Chiffre
            valeur={v.dossiers.total}
            libelle="dossiers"
            detail={`${v.dossiers.pro} pro · ${v.dossiers.particuliers} particulier(s) · ${v.dossiers.supprimes} supprimé(s)`}
          />
        </div>
      </section>

      <section className="adm-esp-carte" aria-labelledby="esp-formules">
        <h2 id="esp-formules">Formules</h2>
        <div className="adm-esp-chiffres">
          <Chiffre valeur={stats.abonnements.payants} libelle="abonnements payants" />
          <Chiffre valeur={stats.abonnements.offerts} libelle="offerts" />
          <Chiffre valeur={stats.abonnements.impayes} libelle="impayés" />
        </div>
        <table className="adm-table adm-esp-petit">
          <thead>
            <tr>
              <th>Produit</th>
              <th>Formule</th>
              <th>Espaces</th>
            </tr>
          </thead>
          <tbody>
            {stats.produits.map((p) => (
              <tr key={`${p.produit}-gratuit`}>
                <td>{PRODUITS[p.produit]}</td>
                <td className="adm-muted">Sans abonnement payant</td>
                <td>{NOMBRE.format(p.espacesGratuits)}</td>
              </tr>
            ))}
            {payantes.map((f) => (
              <tr key={f.id}>
                <td>{PRODUITS[f.produit]}</td>
                <td>
                  {f.nom}
                  {!f.active ? <span className="adm-muted"> (hors vente)</span> : null}
                </td>
                <td>
                  {NOMBRE.format(f.abonnements)}
                  {f.offerts ? (
                    <span className="adm-muted"> dont {f.offerts} offert(s)</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="adm-esp-carte" aria-labelledby="esp-inscriptions">
        <h2 id="esp-inscriptions">Inscriptions</h2>
        <div className="adm-esp-chiffres">
          <Chiffre
            valeur={i.comptes7j}
            libelle="comptes sur 7 jours"
            detail={`${i.comptes30j} sur 30 jours`}
          />
          <Chiffre
            valeur={i.dossiers7j}
            libelle="dossiers sur 7 jours"
            detail={`${i.dossiers30j} sur 30 jours`}
          />
          <Chiffre valeur={i.espaces30j} libelle="espaces sur 30 jours" />
        </div>
        <Courbe points={i.parSemaine} />
      </section>

      <section className="adm-esp-carte" aria-labelledby="esp-activite">
        <h2 id="esp-activite">Activité</h2>
        <div className="adm-esp-chiffres">
          <Chiffre
            valeur={a.connectes7j}
            libelle="connectés sur 7 jours"
            detail={`${a.connectes30j} sur 30 jours`}
          />
          <Chiffre valeur={a.jamaisConnectes} libelle="jamais connectés" />
          <Chiffre valeur={a.adressesNonConfirmees} libelle="adresses non confirmées" />
        </div>
      </section>
    </div>
  );
}

function Abonnements({ liste }: { liste: AbonnementVue[] }) {
  if (liste.length === 0) return <span className="adm-muted">Gratuit</span>;
  return (
    <span className="adm-esp-formules">
      {liste.map((a) => (
        <span
          key={`${a.produit}-${a.formule}`}
          className={`adm-esp-formule${a.statut === 'PAST_DUE' ? ' adm-esp-formule--alerte' : ''}`}
          title={`${PRODUITS[a.produit]} — ${a.formule}, ${STATUTS_ABONNEMENT[a.statut] ?? a.statut}${a.source === 'GIFT' ? ' (offert)' : ''}, jusqu’au ${date(a.finPeriode)}`}
        >
          {a.produit} · {a.formule}
          {a.source === 'GIFT' ? ' (offert)' : ''}
        </span>
      ))}
    </span>
  );
}

function DetailEspace({ detail, onFait }: { detail: Detail; onFait: () => void }) {
  const ouvert = !detail.fermeLe;
  return (
    <div className="adm-esp-detail">
      <section>
        <h3>Utilisateurs ({detail.membres.length})</h3>
        <ul className="adm-esp-liste">
          {detail.membres.map((m) => (
            <li key={m.id}>
              <strong>{m.email}</strong>
              {m.nom ? <span className="adm-muted"> — {m.nom}</span> : null}
              <span className="adm-esp-etiquette">{ROLES[m.role]}</span>
              {m.profil === 'LECTURE_SEULE' ? (
                <span className="adm-esp-etiquette">lecture seule</span>
              ) : null}
              <div className="adm-muted">
                {STATUTS[m.statut]} · dernière connexion {date(m.derniereConnexion)} · membre depuis{' '}
                {date(m.membreDepuis)} ·{' '}
                <Link to={`/admin/comptes?q=${encodeURIComponent(m.email)}`}>Gérer le compte</Link>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h3>Dossiers ({detail.dossiers.filter((d) => !d.supprimeLe).length})</h3>
        {detail.dossiers.length === 0 ? (
          <p className="adm-muted">Aucun dossier.</p>
        ) : (
          <ul className="adm-esp-liste">
            {detail.dossiers.map((d) => (
              <li key={d.id} className={d.supprimeLe ? 'adm-esp-supprime' : undefined}>
                <strong>{d.nom}</strong>
                <span className="adm-esp-etiquette">
                  {d.type === 'PRO' ? 'pro' : 'particulier'}
                </span>
                <div className="adm-muted">
                  {d.siret ? `SIRET ${d.siret}` : 'sans SIRET'}
                  {d.ville ? ` · ${d.codePostal ?? ''} ${d.ville}` : ''}
                  {d.ape ? ` · APE ${d.ape}` : ''} · créé le {date(d.creeLe)}
                  {d.supprimeLe ? ` · fermé le ${date(d.supprimeLe)}` : ''}
                </div>
                {ouvert && !d.supprimeLe ? <FermerDossier companyId={d.id} nom={d.nom} onFait={onFait} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h3>Abonnements</h3>
        {detail.abonnements.length === 0 ? (
          <p className="adm-muted">Aucun : formules gratuites des produits.</p>
        ) : (
          <ul className="adm-esp-liste">
            {detail.abonnements.map((a) => (
              <li key={`${a.produit}-${a.depuis}`}>
                <strong>
                  {PRODUITS[a.produit]} — {a.formule}
                </strong>
                <span className="adm-esp-etiquette">
                  {a.enCours ? (STATUTS_ABONNEMENT[a.statut] ?? a.statut) : 'terminé'}
                </span>
                <div className="adm-muted">
                  {SOURCES[a.source] ?? a.source} · quantité {a.quantite} · depuis le{' '}
                  {date(a.depuis)} · {a.resiliationEnFin ? 'se termine' : 'échéance'} le{' '}
                  {date(a.finPeriode)}
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="adm-muted">
          {detail.compteurs.invitations} invitation(s) en attente · {detail.compteurs.achatsPacks}{' '}
          achat(s) de packs · {detail.compteurs.accesExternes} accès extérieur(s)
        </p>
      </section>
      <section className="adm-esp-detail-fermeture">
        {ouvert ? (
          <FermerEspace spaceId={detail.id} onFait={onFait} />
        ) : (
          <p className="adm-muted">Espace fermé le {date(detail.fermeLe)} : plus personne n’y entre.</p>
        )}
      </section>
    </div>
  );
}

export function AdminEspacesPage() {
  const acces = useAccesAdmin();
  const [stats, setStats] = useState<Statistiques | null>(null);
  const [liste, setListe] = useState<Liste | null>(null);
  const [q, setQ] = useState('');
  const [etat, setEtat] = useState<Etat>('actifs');
  const [formule, setFormule] = useState('');
  const [page, setPage] = useState(1);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [export_, setExport] = useState(false);
  const demande = useRef(0);

  const charger = useCallback(
    async (f: { q: string; etat: Etat; formule: string; page: number }) => {
      const numero = ++demande.current;
      setChargement(true);
      setErreur(null);
      try {
        const res = await adminFetch(`/admin/espaces${parametres(f)}`);
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        const corps = (await res.json()) as Liste;
        // Une réponse plus ancienne qu'une recherche déjà relancée est ignorée.
        if (numero === demande.current) setListe(corps);
      } catch (err) {
        if (numero === demande.current) setErreur(errorMessage(err));
      } finally {
        if (numero === demande.current) setChargement(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!acces.superadmin) return;
    let vivant = true;
    void (async () => {
      try {
        const res = await adminFetch('/admin/espaces/statistiques');
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        const corps = (await res.json()) as Statistiques;
        if (vivant) setStats(corps);
      } catch (err) {
        if (vivant) setErreur(errorMessage(err));
      }
    })();
    return () => {
      vivant = false;
    };
  }, [acces.superadmin]);

  // Recherche au fil de la frappe (300 ms), filtres et page aussitôt.
  useEffect(() => {
    if (!acces.superadmin) return;
    const minuterie = setTimeout(() => void charger({ q, etat, formule, page }), q ? 300 : 0);
    return () => clearTimeout(minuterie);
  }, [acces.superadmin, charger, q, etat, formule, page]);

  if (!acces.superadmin) return <Navigate to="/admin/tickets" replace />;

  async function basculer(id: string) {
    if (ouvert === id) {
      setOuvert(null);
      return;
    }
    setOuvert(id);
    if (details[id]) return;
    try {
      const res = await adminFetch(`/admin/espaces/${id}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const corps = (await res.json()) as Detail;
      setDetails((actuels) => ({ ...actuels, [id]: corps }));
    } catch (err) {
      setErreur(errorMessage(err));
    }
  }

  /** Après une fermeture ou une purge : détail rechargé, liste et statistiques à jour. */
  function apresAction(id?: string) {
    if (id) {
      setDetails((actuels) => {
        const { [id]: _retire, ...reste } = actuels;
        void _retire;
        return reste;
      });
      setOuvert(null);
    }
    void charger({ q, etat, formule, page });
  }

  async function exporter() {
    setExport(true);
    setErreur(null);
    try {
      const res = await adminFetch(`/admin/espaces/export${parametres({ q, etat, formule })}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const url = URL.createObjectURL(await res.blob());
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = `espaces-semply-${new Date().toISOString().slice(0, 10)}.csv`;
      lien.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setExport(false);
    }
  }

  const formulesFiltre = (stats?.formules ?? []).filter((f) => !f.gratuite);

  return (
    <div className="adm-page">
      <h1>Espaces</h1>
      <p className="adm-sub">
        Les espaces de la suite, leurs utilisateurs et leurs dossiers. Pour suspendre ou réactiver
        un compte, ouvrez l’espace puis « Gérer le compte ».
      </p>

      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}

      {stats ? (
        <BlocStatistiques stats={stats} />
      ) : (
        <p role="status">Chargement des statistiques…</p>
      )}

      <PanneauPurges onFait={() => apresAction()} />

      <form
        className="adm-ajout adm-esp-filtres"
        role="search"
        onSubmit={(e) => e.preventDefault()}
      >
        <label>
          Rechercher
          <input
            type="search"
            value={q}
            maxLength={200}
            placeholder="Espace, e-mail, nom, dossier, SIRET…"
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          État
          <select
            value={etat}
            onChange={(e) => {
              setEtat(e.target.value as Etat);
              setPage(1);
            }}
          >
            <option value="actifs">Espaces actifs</option>
            <option value="fermes">Espaces fermés</option>
            <option value="tous">Tous</option>
          </select>
        </label>
        <label>
          Formule
          <select
            value={formule}
            onChange={(e) => {
              setFormule(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toutes</option>
            <option value="sans">Sans abonnement</option>
            {formulesFiltre.map((f) => (
              <option key={f.id} value={f.id}>
                {PRODUITS[f.produit]} — {f.nom}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn btn-secondary"
          type="button"
          disabled={export_}
          onClick={() => void exporter()}
        >
          {export_ ? 'Export…' : 'Exporter en CSV'}
        </button>
      </form>

      {liste === null ? (
        <p role="status">Chargement…</p>
      ) : (
        <>
          <p className="adm-muted" role="status">
            {chargement ? 'Recherche…' : `${NOMBRE.format(liste.total)} espace(s)`}
          </p>
          {liste.espaces.length === 0 ? (
            <p>Aucun espace ne correspond.</p>
          ) : (
            <div className="adm-esp-defilement">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Espace</th>
                    <th>Propriétaire</th>
                    <th>Utilisateurs</th>
                    <th>Dossiers</th>
                    <th>Formules</th>
                    <th>Dernière connexion</th>
                    <th>
                      <span className="adm-esp-masque">Détail</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {liste.espaces.map((e) => (
                    <EspaceLigne
                      key={e.id}
                      espace={e}
                      ouvert={ouvert === e.id}
                      detail={details[e.id]}
                      basculer={() => void basculer(e.id)}
                      onFait={() => apresAction(e.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {liste.pages > 1 && (
            <nav className="adm-esp-pages" aria-label="Pages">
              <button
                className="btn btn-secondary btn-small"
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Précédente
              </button>
              <span>
                Page {liste.page} / {liste.pages}
              </span>
              <button
                className="btn btn-secondary btn-small"
                type="button"
                disabled={page >= liste.pages}
                onClick={() => setPage(page + 1)}
              >
                Suivante
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

function EspaceLigne({
  espace: e,
  ouvert,
  detail,
  basculer,
  onFait,
}: {
  espace: EspaceListe;
  ouvert: boolean;
  detail: Detail | undefined;
  basculer: () => void;
  onFait: () => void;
}) {
  return (
    <>
      <tr className={e.fermeLe ? 'adm-esp-supprime' : undefined}>
        <td>
          <strong>{e.nom}</strong>
          <div className="adm-muted">
            créé le {date(e.creeLe)}
            {e.fermeLe ? ` · fermé le ${date(e.fermeLe)}` : ''}
          </div>
        </td>
        <td>
          {e.proprietaire ? (
            <>
              {e.proprietaire.email}
              {e.proprietaire.nom ? <div className="adm-muted">{e.proprietaire.nom}</div> : null}
            </>
          ) : (
            '—'
          )}
        </td>
        <td>{e.membres}</td>
        <td>{e.dossiers}</td>
        <td>
          <Abonnements liste={e.abonnements} />
        </td>
        <td>{date(e.derniereConnexion)}</td>
        <td className="adm-esp-action">
          <button
            className="btn btn-secondary btn-small"
            type="button"
            aria-expanded={ouvert}
            onClick={basculer}
          >
            {ouvert ? 'Fermer' : 'Ouvrir'}
          </button>
        </td>
      </tr>
      {ouvert && (
        <tr className="adm-esp-ligne-detail">
          <td colSpan={7}>
            {detail ? <DetailEspace detail={detail} onFait={onFait} /> : <p role="status">Chargement…</p>}
          </td>
        </tr>
      )}
    </>
  );
}
